import { DurationUnitType, Duration as daysJsDuration } from "dayjs/plugin/duration";

const culture = "cs-CZ"; // can be from browser

export namespace DateTimeUtils {
    export interface Duration {
        /** Days */
        d: number;
        /** Hours */
        h: number;
        /** Minutes */
        m: number;
        /** Seconds */
        s: number;
    }

    type UnitType = "year" | "month" | "day" | "hour" | "minute" | "second";

    /**
     * Formats a given date for a blog post.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted date string.
     */
    export function formatBlogDate(date: Date) {
        if (isToday(date)) {
            return `Dnes | ${formatTime(date)}`;
        } else if (isTomorrow(date)) {
            return `Včera | ${formatTime(date)}`;
        }
        return `${formatWeekday(date)} | ${formatDate(date)}`;
    }

    /**
     * Formats the given date to display the long name of the weekday.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted weekday name.
     */
    export function formatWeekday(date: Date) {
        return format(date, { weekday: "long" });
    }

    /**
     * Formats the given date with the specified year format.
     *
     * @param {Date} date - The date to be formatted.
     * @param {"numeric" | "2-digit"} year - The format of the year part.
     * @return {string} The formatted date string.
     */
    export function formatDate(date: Date | number, year: "numeric" | "2-digit" = "numeric") {
        return format(date, { year: year, month: "numeric", day: "numeric" });
    }

    /**
     * Formats the given date with a short format including month and day.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted date string.
     */
    export function formatDateShort(date: Date) {
        return format(date, { month: "numeric", day: "numeric" });
    }

    /**
     * Formats the given date without the year part.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted date string without the year.
     */
    export function formatDateWithoutYear(date: Date) {
        return format(date, { month: "2-digit", day: "2-digit" });
    }

    /**
     * Formats the given date to display the time with hour and minute.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted time string with hour and minute.
     */
    export function formatTime(date: Date) {
        return format(date, { hour: "numeric", minute: "2-digit" });
    }

    /**
     * Formats the given date to display the time in a short format.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted time string.
     */
    export function formatTimeShort(date: Date) {
        return format(date, { hour: "numeric" });
    }

    /**
     * Formats the given date to display the time in a long format.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted time string.
     */
    export function formatTimeLong(date: Date) {
        return format(date, { hour: "numeric", minute: "2-digit", second: "2-digit" });
    }

    /**
     * Formats the given date and time into a long format including year, month, day, hour, and minute.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted date and time string.
     */
    export function formatDateTimeLong(date: Date) {
        return format(date, { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
    }

    /**
     * Formats the given date and time into a short format including month, day, hour, and minute.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted date and time string.
     */
    export function formatDateTimeShort(date: Date) {
        return format(date, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
    }

    /**
     * Formats the given date with the weekday included in the output.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted date string with the weekday.
     */
    export function formatDateWithWeekday(date: Date) {
        return format(date, { month: "numeric", day: "numeric", weekday: "long" });
    }

    /**
     * Formats the given date with the shortened weekday included in the output.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted date string with the weekday.
     */
    export function formatDateWithWeekdayShort(date: Date) {
        return format(date, { month: "numeric", day: "numeric", weekday: "short" });
    }

    export function formatLongWeekdayEn(date: number | Date) {
        return formatEn(date, { weekday: "long" });
    }

    /**
     * Formats a given date with the corresponding day of the week.
     *
     * @param {number | Date} date - The date to be formatted. It can be either a number representing the number of milliseconds since January 1, 1970, 00:00:00 UTC, or a Date object.
     * @return {string} The formatted date string with the corresponding day of the week. If the input date is falsy, an empty string is returned.
     */
    export function formatDateWithDay(date: number | Date) {
        if (!date) {
            return "";
        }

        const dateAsDate = new Date(date);
        const currentDateString = formatDate(dateAsDate);

        switch (dateAsDate.getDay()) {
            case 0:
                return "v neděli, " + currentDateString;
            case 1:
                return "v pondělí, " + currentDateString;
            case 2:
                return "v úterý, " + currentDateString;
            case 3:
                return "ve středu, " + currentDateString;
            case 4:
                return "ve čtvrtek, " + currentDateString;
            case 5:
                return "v pátek, " + currentDateString;
            case 6:
                return "v sobotu, " + currentDateString;
            default:
                return currentDateString;
        }
    }

    /**
     * Formats the given date with the year, month, day, and long weekday.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted date string with year, month, day, and long weekday.
     */
    export function formatLongDateWithWeekday(date: Date) {
        return format(date, { year: "numeric", month: "numeric", day: "numeric", weekday: "long" });
    }

    /**
     * Formats the given date into a local ISO string including year, month, day, hour, minute, and second.
     *
     * @param {Date} date - The date to be formatted.
     * @return {string} The formatted local ISO string.
     */
    export function getLocalIsoString(date: Date) {
        const monthStr = addLeadingZero(date.getMonth() + 1);
        const dayStr = addLeadingZero(date.getDate());
        const hourStr = addLeadingZero(date.getHours());
        const minStr = addLeadingZero(date.getMinutes());
        const secStr = addLeadingZero(date.getSeconds());
        return `${date.getFullYear()}-${monthStr}-${dayStr}T${hourStr}:${minStr}:${secStr}`;
    }

    /**
     * Formats a countdown in seconds into a human-readable string. If the countdown is less than 1 day,
     * it formats the countdown as a duration using the formatDuration function. Otherwise, it formats
     * the countdown as the number of days followed by the appropriate pluralization of the word "days".
     *
     * @param {number} seconds - The countdown in seconds.
     * @return {string} The formatted countdown as a string.
     */
    export function formatCountdown(seconds: number) {
        let days = seconds;
        let text = "";
        if (seconds < 3_600 * 24) {
            text = formatDuration(seconds * 1000);
        } else {
            days = Math.floor(seconds / 3600 / 24);

            text = `${days} ${formatAfterXDaysLabel(days)}`;
        }

        return text;
    }

    /**
     * Formats a countdown in seconds into a human-readable string with additional details for days and hours.
     *
     * @param {number} seconds - The countdown in seconds.
     * @return {string} The formatted countdown as a string.
     */
    export function formatCountdownLong(seconds: number) {
        const days = Math.floor(seconds / 3600 / 24);
        const hours = Math.floor((seconds - days * 3600 * 24) / 3600);

        let text = formatCountdown(seconds);

        if (days > 0 && hours > 0) {
            text += `, ${hours} ${formatAfterXHoursLabel(hours)}`;
        }

        return text;
    }

    /**
     * Formats the given duration in milliseconds into a human-readable string representation.
     *
     * @param {number} millis - The duration in milliseconds.
     * @return {string} The formatted duration string in the format "HH:MM:SS" or "MM:SS" if less than an hour.
     */
    export function formatDuration(millis: number, longFormat = false) {
        const seconds = Math.floor(millis / 1000);
        let str = "";
        if (seconds < 10) {
            str = "0:0" + seconds;
        } else if (seconds < 60) {
            str = "0:" + seconds;
        } else if (seconds < 3600) {
            str = getMinutes(seconds) + ":";
            const second = getSeconds(seconds);
            str += second < 10 ? "0" + second : second;
        } else {
            str = getHours(seconds) + ":";
            const minutes = getMinutes(seconds);
            const second = getSeconds(seconds);
            str += minutes < 10 ? "0" + minutes : minutes;
            str += ":";
            str += second < 10 ? "0" + second : second;
        }
        if (longFormat) {
            const mask = "00:00:00";
            str = mask.substring(0, mask.length - str.length) + str;
        }
        return str;
    }

    /**
     * Formats the label for the number of days after a specific count.
     *
     * @param {number} dayCount - The number of days.
     * @return {string} The formatted label for the number of days.
     */
    export function formatAfterXDaysLabel(dayCount: number) {
        switch (dayCount) {
            case 1:
                return "den";
            case 2:
            case 3:
            case 4:
                return "dny";
            default:
                return "dnů";
        }
    }

    /**
     * Formats the label for the number of hours after a specific count.
     *
     * @param {number} hoursCount - The number of hours.
     * @return {string} The formatted label for the number of hours.
     */
    export function formatAfterXHoursLabel(hoursCount: number) {
        switch (hoursCount) {
            case 1:
                return "hodinu";
            case 2:
            case 3:
            case 4:
                return "hodiny";
            default:
                return "hodin";
        }
    }

    /**
     * Formats the label for the number of minutes after a specific count.
     *
     * @param {number} minutesCount - The number of minutes.
     * @return {string} The formatted label for the number of minutes.
     */
    export function formatAfterXMinutesLabel(minutesCount: number) {
        switch (minutesCount) {
            case 1:
                return "minutu";
            case 2:
            case 3:
            case 4:
                return "minuty";
            default:
                return "minut";
        }
    }

    /**
     *
     * @param {number} dayOfWeek day of week starting with 0 as sunday - date.getDay()
     * @param {boolean} useOnPrefixInsteadOfAt - If true prefix "na" is used otherwise prefix "v/ve" is used
     */
    export function atWeekdayLabel(dayOfWeek: number, useOnPrefixInsteadOfAt = false) {
        if (dayOfWeek > 6 || dayOfWeek < 0) {
            return "";
        }

        const atPrefix = dayOfWeek == 3 || dayOfWeek == 4 ? "ve" : "v";
        const prefix = useOnPrefixInsteadOfAt ? "na" : atPrefix;
        const dayOfWeekName = ["neděli", "pondělí", "úterý", "středu", "čtvrtek", "pátek", "sobotu"][dayOfWeek];

        return `${prefix}&nbsp;${dayOfWeekName}`;
    }

    /**
     * Concatenates the weekday label and formatted date.
     *
     * @param {Date} date - The date to be used for concatenation.
     * @return {string} The concatenated string of weekday label and formatted date.
     */
    export function atWeekdayWithDate(date: Date) {
        return `${atWeekdayLabel(date.getDay())}, ${formatDate(date)}`;
    }

    /**
     * Determines the time frame based on the input time frame string.
     *
     * @param {string} timeFrame - The input time frame string.
     * @return {string} The corresponding time frame label: "ráno", "odpoledne", or "večer".
     */
    export function getTimeFrames(hour: number) {
        if (hour < 12) {
            return "ráno";
        } else if (hour === 12) {
            return "poledne";
        } else if (hour < 18) {
            return "odpoledne";
        } else {
            return "večer";
        }
    }

    /**
     * Concatenates the weekday label, formatted date, and time frame label into a single string.
     *
     * @param {Date} date - The date to be used for concatenation.
     * @return {string} The concatenated string of weekday label, formatted date, and time frame label.
     */
    export function atWeekdayWithDateAndTimeframe(date: Date) {
        return `${atWeekdayLabel(date.getDay())}, ${formatDate(date)}, (${getTimeFrames(date.getHours())})`;
    }

    /**
     * Calculates the duration in days, hours, minutes, and seconds based on the provided milliseconds.
     *
     * @param {number} millis - The duration in milliseconds.
     * @return {Duration} The object containing the calculated duration in days, hours, minutes, and seconds.
     */
    export function getDuration(millis: number): Duration {
        const seconds = Math.floor(millis / 1000);
        const duration = {
            d: 0,
            h: 0,
            m: getMinutes(seconds),
            s: getSeconds(seconds),
        };

        let h = getHours(seconds);
        const d = Math.floor(h / 24);
        h = h % 24;
        duration.d = d;
        duration.h = h;
        return duration;
    }

    /**
     * Trims the time part of the given date to midnight (00:00:00.000).
     *
     * @param {Date} date - The date to trim the time from.
     * @return {Date} The new Date object with the time set to midnight.
     */
    export function trimTime(date: Date) {
        const copy = new Date(date.getTime());
        copy.setMilliseconds(0);
        copy.setSeconds(0);
        copy.setMinutes(0);
        copy.setHours(0);
        return copy;
    }

    /**
     * Formats the given count of time units into the 6th form based on the unit type.
     *
     * @param {number} unitsCount - The count of units to be formatted.
     * @param {UnitType} unitType - The type of unit to format.
     * @return {string} The formatted count with the appropriate unit.
     */
    export function formatDateUnit6thForm(unitsCount: number, unitType: UnitType, withCount = true) {
        let formattedUnit = "";

        switch (unitType) {
            case "year":
                formattedUnit = unitsCount === 1 ? "rokem" : "lety";
                break;
            case "month":
                formattedUnit = unitsCount === 1 ? "měsícem" : "měsíci";
                break;
            case "day":
                formattedUnit = unitsCount === 1 ? "dnem" : "dny";
                break;
            case "hour":
                formattedUnit = unitsCount === 1 ? "hodinou" : "hodinami";
                break;
            case "minute":
                formattedUnit = unitsCount === 1 ? "minutou" : "minutami";
                break;
            case "second":
                formattedUnit = unitsCount === 1 ? "sekundou" : "sekundami";
                break;
        }

        return withCount ? `${unitsCount} ${formattedUnit}` : formattedUnit;
    }

    export function formatTimeAgo(date: Date, referenceDate = new Date()): string {
        const diffInMs = referenceDate.getTime() - date.getTime();
        const diffInSeconds = Math.floor(diffInMs / 1000);

        // Placed before the for loop, because it is the most common case
        if (diffInSeconds < 60) return "Právě teď";

        const timeUnits: { unit: UnitType; value: number }[] = [
            { unit: "year", value: Math.floor(diffInSeconds / (60 * 60 * 24 * 30 * 12)) },
            { unit: "month", value: Math.floor(diffInSeconds / (60 * 60 * 24 * 30)) },
            { unit: "day", value: Math.floor(diffInSeconds / (60 * 60 * 24)) },
            { unit: "hour", value: Math.floor(diffInSeconds / (60 * 60)) },
            { unit: "minute", value: Math.floor(diffInSeconds / 60) },
        ];

        const getPrefix = (num: number) => (num > 1 ? `Před ${num} ` : "Před ");

        for (const { unit, value } of timeUnits) {
            if (value > 0) {
                if (unit === "day" && value === 1) return "Včera";
                if (unit === "day") return getPrefix(Math.min(value, 7)) + formatDateUnit6thForm(value, unit, false);
                if (unit === "minute" && value > 10)
                    return getPrefix(Math.floor(value / 10) * 10) + formatDateUnit6thForm(value, unit, false);
                return getPrefix(value) + formatDateUnit6thForm(value, unit, false);
            }
        }

        return "";
    }

    /**
     * Checks if the given date is today.
     *
     * @param {Date} date - The date to check.
     * @return {boolean} True if the given date is today, false otherwise.
     */
    export function isToday(date: Date) {
        return getDateOnly(new Date()).getTime() === getDateOnly(date).getTime();
    }

    /**
     * Checks if the given date is tomorrow.
     *
     * @param {Date} date - The date to check if it is tomorrow.
     * @return {boolean} True if the given date is tomorrow, false otherwise.
     */
    export function isTomorrow(date: Date) {
        const tomorrow = getDateOnly(new Date());
        tomorrow.setDate(tomorrow.getDate() - 1);
        return tomorrow.getTime() === getDateOnly(date).getTime();
    }

    /**
     * Returns the length of a unit in a countdown duration.
     *
     * @param {daysJsDuration} timeRemaining - The duration of the countdown.
     * @param {DurationUnitType} unit - The unit for which to calculate the length.
     * @param {boolean} getTotalHours - Whether to return the total number of hours or the remaining hours.
     * @return {number} The length of the unit in the countdown duration.
     */
    export function getCountdownUnitLength(
        timeRemaining: daysJsDuration,
        unit: DurationUnitType,
        getTotalHours: boolean
    ) {
        switch (unit) {
            case "days":
                return Math.floor(timeRemaining.asDays());
            case "hours":
                return getTotalHours ? Math.floor(timeRemaining.asHours()) : timeRemaining.get(unit);
            case "seconds": {
                const seconds = timeRemaining.get(unit);
                return String(seconds).length === 1 ? `0${seconds}` : seconds;
            }
            default:
                return timeRemaining.get(unit);
        }
    }

    /**
     * Returns the label for a countdown unit based on the unit type and value.
     *
     * @param {DurationUnitType} unit - The unit type of the countdown.
     * @param {unknown} value - The value of the countdown unit.
     * @return {string} The label corresponding to the countdown unit.
     */
    export function getCountdownUnitLabel(unit: DurationUnitType, value: unknown) {
        const intValue = parseInt(value as string);
        let text: string;

        switch (unit) {
            case "days":
                if (intValue >= 5) text = "dnů";
                else if (intValue >= 2) text = "dny";
                else if (intValue === 1) text = "den";
                else text = "dnů";
                break;
            case "hours":
                if (intValue >= 5) text = "hodin";
                else if (intValue >= 2) text = "hodiny";
                else if (intValue === 1) text = "hodinu";
                else text = "hodin";
                break;
            case "minutes":
                if (intValue >= 5) text = "minut";
                else if (intValue >= 2) text = "minuty";
                else if (intValue === 1) text = "minutu";
                else text = "minut";
                break;
            case "seconds":
                if (intValue >= 5) text = "vteřin";
                else if (intValue >= 2) text = "vteřiny";
                else if (intValue === 1) text = "vteřinu";
                else text = "vteřin";
        }
        return text;
    }

    function format(date: Date | number, options: Intl.DateTimeFormatOptions) {
        return new Intl.DateTimeFormat(culture, options).format(date);
    }

    function formatEn(date: number | Date, options: Intl.DateTimeFormatOptions) {
        return new Intl.DateTimeFormat("en-UK", options).format(date);
    }

    function getDateOnly(date: Date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    }

    function getSeconds(num: number): number {
        return num % 60;
    }

    function getMinutes(num: number): number {
        return Math.floor(num / 60) % 60;
    }

    function getHours(num: number): number {
        return Math.floor(num / 3600);
    }

    function addLeadingZero(num: number) {
        return num <= 9 ? "0" + num : num.toString();
    }
}
